import { useQuery } from '@tanstack/react-query';
import type { YearWithProjects, Client, User, Tag, Project, SequenceTemplate, EmailAccount, StatusConfig } from '@shared/schema';

// Minimal shared query hooks for basic data fetching
// These hooks provide simple wrappers for common queries

// Stable query keys shared across parallel/prefetch data hooks.
export const QUERY_KEYS = {
  PROJECTS: ['/api/projects'] as const,
  CLIENTS: ['/api/clients'] as const,
  EMPLOYEES: ['/api/artists'] as const,
  YEARS: ['/api/years'] as const,
  TAGS: ['/api/tags'] as const,
  SEQUENCE_TEMPLATES: ['/api/sequence-templates'] as const,
  EMAIL_ACCOUNTS: ['/api/email-accounts'] as const,
  STATUS_CONFIGS_PULSE: ['/api/status-configs', 'pulse'] as const,
  STATUS_CONFIGS_PROJECT: ['/api/status-configs', 'project'] as const,
} as const;

export function useYears() {
  return useQuery<YearWithProjects[]>({
    queryKey: ['/api/years'],
    staleTime: 60 * 1000,
  });
}

export function useClients() {
  return useQuery<Client[]>({
    queryKey: ['/api/clients'],
    staleTime: 60 * 1000,
  });
}

export function useArtists() {
  return useQuery<User[]>({
    queryKey: ['/api/artists'],
    staleTime: 60 * 1000,
  });
}

export function useTags() {
  return useQuery<Tag[]>({
    queryKey: ['/api/tags'],
    staleTime: 60 * 1000,
  });
}

export function useSequenceTemplates() {
  return useQuery<SequenceTemplate[]>({
    queryKey: ['/api/sequence-templates'],
    staleTime: 60 * 1000,
  });
}

export function useProjects(options?: { enabled?: boolean }) {
  return useQuery<Project[]>({
    queryKey: ['/api/projects'],
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useEmailAccounts() {
  return useQuery<EmailAccount[]>({
    queryKey: ['/api/email-accounts'],
    staleTime: 60 * 1000,
  });
}

export function useStatusConfigs(category?: string) {
  return useQuery<StatusConfig[]>({
    queryKey: category ? ['/api/status-configs', category] : ['/api/status-configs'],
    staleTime: 60 * 1000,
  });
}

// Paginated projects response shape returned by GET /api/projects?paginate=true
interface PaginatedProjectsResponse {
  data: any[];
  total: number;
  hasMore: boolean;
}

export function useProjectsPaginated(page: number, pageSize: number, fields?: string[]) {
  return useQuery<PaginatedProjectsResponse>({
    queryKey: ['/api/projects', 'paginated', page, pageSize, fields],
    staleTime: 30 * 1000,
  });
}
